import { RefObject, useCallback } from "react";

import BulkActions from "components/BulkActions";
import BulkActionsDrawer from "components/BulkActions/Drawer";
import BulkActionsDrawerActionSteps from "components/BulkActions/Drawer/ActionSteps";
import BulkActionsDrawerActionStepsHeader from "components/BulkActions/Drawer/ActionSteps/Header";
import BulkActionsDrawerActionStepsInfoBanner from "components/BulkActions/Drawer/ActionSteps/InfoBanner";
import BulkActionsDrawerResultsStep from "components/BulkActions/Drawer/ResultsStep";
import BulkActionsDrawerResultsStepHeader from "components/BulkActions/Drawer/ResultsStep/Header";
import BulkActionsDrawerResultsStepBody from "components/BulkActions/Drawer/ResultsStep/Body";
import BulkActionsDrawerResultsStepBodyEmptyTab from "components/BulkActions/Drawer/ResultsStep/Body/EmptyTab";
import BulkActionsDrawerResultsStepFooter from "components/BulkActions/Drawer/ResultsStep/Footer";
import BulkActionsFloatingBar from "components/BulkActions/FloatingBar";
import BulkActionsFloatingBarHeader from "components/BulkActions/FloatingBar/Header";
import BulkActionsFloatingBarSteps from "components/BulkActions/FloatingBar/Steps";
import { type BulkActionItemTriggerResult } from "components/BulkActions/helpers";
import {
  BulkActionItemID,
  BulkActionResultTabs,
  BulkActionsResult,
  BulkActionsStep,
  BulkActionsVariant,
} from "components/BulkActions/types";
import useBulkActions from "components/BulkActions/useBulkActions";
import useExecutionQueue from "components/BulkActions/useExecutionQueue";
import useAnalytics from "hooks/useAnalytics";
import BulkActionsPatternsActionsList from "components/BulkActionsPatterns/ActionsList";
import { useResultTabItems } from "components/BulkActions/useResultTabItems";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";

import IdpGroupRolesBulkActionsConfirmActionForm from "./ConfirmActionForm";
import IdpGroupRolesBulkActionsConfirmationView from "./ConfirmationView";
import IdpGroupRolesBulkActionsResultItem from "./ResultItem";
import IdpGroupRolesBulkActionsSelectedItemsView from "./SelectedItemsView";
import { IdpGroupRolesBulkActionsAnalyticsPayload } from "./types";
import useIdpGroupRolesBulkActions from "./useIdpGroupRolesBulkActions";
import { IdpGroupRolesMutationVariables } from "./useIdpGroupRolesBulkActions/types";
import { RoleBinding } from "../types";

type IdpGroupRolesBulkActionsProps = {
  virtualizedListContainerRef?: RefObject<HTMLElement | null>;
  selectedSet: Set<BulkActionItemID>;
  idpGroupRolesMap: Map<BulkActionItemID, RoleBinding>;
  onBulkResetAll: () => void;
  onItemDismiss: (id: BulkActionItemID) => void;
  onFinish: () => Promise<unknown>;
};

const IdpGroupRolesBulkActions = ({
  selectedSet,
  idpGroupRolesMap,
  onBulkResetAll,
  onItemDismiss,
  virtualizedListContainerRef,
  onFinish,
}: IdpGroupRolesBulkActionsProps) => {
  const selectedItemsCount = selectedSet.size;

  const idpGroupRolesBulkActions = useIdpGroupRolesBulkActions();
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationIdpGroupRoles,
  });

  const {
    selectedBulkAction,
    resetSelectedAction,
    availableActions,
    itemsPerAction,
    actionsPerItem,
    selectedEntityItems,
    applicableSelectedEntityItems,
    skippedSelectedEntityItems,
  } = useBulkActions({
    actions: idpGroupRolesBulkActions,
    selectedSet,
    entityItemsMap: idpGroupRolesMap,
  });

  const onItemTrigger = useCallback(
    (
      id: BulkActionItemID,
      mutationVariables: IdpGroupRolesMutationVariables
    ): Promise<BulkActionItemTriggerResult> => {
      const roleBinding = idpGroupRolesMap.get(id);
      if (!roleBinding || !selectedBulkAction) {
        return Promise.reject();
      }
      if (!selectedBulkAction.condition(roleBinding).result) {
        return Promise.resolve(BulkActionsResult.Skipped);
      }
      return selectedBulkAction.mutation(roleBinding, mutationVariables);
    },
    [idpGroupRolesMap, selectedBulkAction]
  );

  const {
    triggerExecution,
    bulkActionItemResults,
    stopExecution,
    stopExecutionForItem,
    bulkActionResultsMetadata,
    resetBulkActionsExecutionQueue,
  } = useExecutionQueue({
    onItemTrigger,
    onAllItemsFinished: onFinish,
    analyticsPage: AnalyticsPageOrganization.OrganizationIdpGroupRoles,
  });

  const { resultsTabItems, saveEntityMap, resetEntityMap } =
    useResultTabItems<RoleBinding>(bulkActionItemResults);

  const handleClose = useCallback(() => {
    onBulkResetAll();
    resetSelectedAction();
    stopExecution();
    resetBulkActionsExecutionQueue();
    resetEntityMap();
  }, [
    onBulkResetAll,
    resetBulkActionsExecutionQueue,
    resetEntityMap,
    resetSelectedAction,
    stopExecution,
  ]);

  const handleOnConfirm = useCallback(
    (
      mutationVariables: IdpGroupRolesMutationVariables,
      analyticsPayload: IdpGroupRolesBulkActionsAnalyticsPayload
    ) => {
      if (selectedBulkAction) {
        const items = itemsPerAction.get(selectedBulkAction.key) || [];
        const skippedItems = skippedSelectedEntityItems.map((item) => item.id);
        triggerExecution(items, mutationVariables, skippedItems);
        saveEntityMap(idpGroupRolesMap);
        trackSegmentAnalyticsEvent("Bulk Actions - Action Confirmed", {
          applicableCount: items?.length || 0,
          notApplicableCount: skippedItems.length,
          action: selectedBulkAction.key,
          ...analyticsPayload,
        });
      }
    },
    [
      itemsPerAction,
      saveEntityMap,
      selectedBulkAction,
      skippedSelectedEntityItems,
      idpGroupRolesMap,
      trackSegmentAnalyticsEvent,
      triggerExecution,
    ]
  );

  const handleOnCancel = (variant: BulkActionsVariant) => () => {
    if (selectedBulkAction) {
      resetSelectedAction();
      const items = itemsPerAction.get(selectedBulkAction.key);
      trackSegmentAnalyticsEvent("Bulk actions - Action Cancelled", {
        applicableCount: items?.length || 0,
        notApplicableCount: skippedSelectedEntityItems.length,
        action: selectedBulkAction.key,
        view: variant,
      });
    }
  };

  const handleContinueWith = useCallback(() => {
    resetSelectedAction();
    resetBulkActionsExecutionQueue();
  }, [resetSelectedAction, resetBulkActionsExecutionQueue]);

  return (
    <BulkActions
      selectedItemsCount={selectedItemsCount}
      onClose={handleClose}
      onContinueWith={handleContinueWith}
      isExecutingBulkActions={!!resultsTabItems[BulkActionResultTabs.Pending].count}
    >
      <BulkActionsFloatingBar virtualizedListContainerRef={virtualizedListContainerRef}>
        <BulkActionsFloatingBarHeader
          selectedItemsCount={selectedItemsCount}
          applicableItemsCount={
            selectedBulkAction?.key ? itemsPerAction.get(selectedBulkAction.key)?.length : 0
          }
          analyticsPage={AnalyticsPageOrganization.OrganizationIdpGroupRoles}
        />
        <BulkActionsFloatingBarSteps>
          {!selectedBulkAction && (
            <BulkActionsPatternsActionsList
              variant={BulkActionsVariant.FloatingBar}
              availableActions={availableActions}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <IdpGroupRolesBulkActionsConfirmActionForm
              variant={BulkActionsVariant.FloatingBar}
              action={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.FloatingBar)}
            />
          )}
        </BulkActionsFloatingBarSteps>
      </BulkActionsFloatingBar>

      <BulkActionsDrawer>
        <BulkActionsDrawerActionSteps>
          <BulkActionsDrawerActionStepsHeader
            analyticsPage={AnalyticsPageOrganization.OrganizationIdpGroupRoles}
            step={
              !selectedBulkAction
                ? BulkActionsStep.ChooseAction
                : BulkActionsStep.ActionConfirmation
            }
          />
          <BulkActionsDrawerActionStepsInfoBanner />

          {!selectedBulkAction && (
            <IdpGroupRolesBulkActionsSelectedItemsView
              items={selectedEntityItems}
              onItemDismiss={onItemDismiss}
              availableActions={availableActions}
              actionsPerItem={actionsPerItem}
              onEmptyActionsCancel={handleClose}
            />
          )}
          {selectedBulkAction && (
            <IdpGroupRolesBulkActionsConfirmationView
              applicableItems={applicableSelectedEntityItems}
              skippedItems={skippedSelectedEntityItems}
              onItemDismiss={onItemDismiss}
              selectedBulkAction={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.Drawer)}
            />
          )}
        </BulkActionsDrawerActionSteps>

        <BulkActionsDrawerResultsStep>
          {(props) => (
            <>
              <BulkActionsDrawerResultsStepHeader
                title={selectedBulkAction?.resultTitle || "Results"}
                analyticsPage={AnalyticsPageOrganization.OrganizationIdpGroupRoles}
              />
              <BulkActionsDrawerResultsStepBody {...props} counter={resultsTabItems}>
                {({ currentTab }) =>
                  resultsTabItems[currentTab].count === 0 ? (
                    <BulkActionsDrawerResultsStepBodyEmptyTab currentTab={currentTab} />
                  ) : (
                    resultsTabItems[currentTab].entitiesList.map(({ status, items }) =>
                      items.map((roleBinding) => (
                        <IdpGroupRolesBulkActionsResultItem
                          key={roleBinding.id}
                          tab={currentTab}
                          roleBinding={roleBinding}
                          resultsMetadata={bulkActionResultsMetadata}
                          stopExecution={() => stopExecutionForItem(roleBinding.id)}
                          status={status}
                        />
                      ))
                    )
                  )
                }
              </BulkActionsDrawerResultsStepBody>
              <BulkActionsDrawerResultsStepFooter
                {...props}
                bulkActionItemResults={bulkActionItemResults}
                stopExecution={stopExecution}
                analyticsPage={AnalyticsPageOrganization.OrganizationIdpGroupRoles}
              />
            </>
          )}
        </BulkActionsDrawerResultsStep>
      </BulkActionsDrawer>
    </BulkActions>
  );
};

export default IdpGroupRolesBulkActions;
